<template>
  <v-container class="blog-container">
    <v-row>
      <!-- Categories Sidebar -->
      <v-col cols="12" md="3">
        <v-card class="categories-card" elevation="2">
          <v-card-title class="text-h6 bg-primary d-flex align-center">
            <v-icon start>mdi-post</v-icon>
            {{ $t('blog.title') }}
          </v-card-title>
          <v-list density="compact" nav>
            <v-list-item
              :active="!filters.category"
              @click="selectCategory(null)"
            >
              <template #prepend>
                <v-icon>mdi-view-dashboard</v-icon>
              </template>
              <v-list-item-title>{{ $t('blog.allCategories') }}</v-list-item-title>
            </v-list-item>

            <v-divider class="my-1" />

            <v-list-item
              v-for="cat in categories"
              :key="cat.categoryId"
              :active="filters.category === cat.slug"
              @click="selectCategory(cat.slug)"
            >
              <template #prepend>
                <v-icon :color="cat.color">{{ cat.icon }}</v-icon>
              </template>
              <v-list-item-title>{{ $t(`blog.categories.${cat.slug}`) }}</v-list-item-title>
              <template #append>
                <div class="d-flex align-center ga-1">
                  <v-badge
                    v-if="cat.isFollowing && cat.unreadCount > 0"
                    :content="cat.unreadCount"
                    color="error"
                    inline
                  />
                  <v-chip size="x-small" variant="text">{{ cat.postCount }}</v-chip>
                  <v-btn
                    :icon="cat.isFollowing ? 'mdi-bell' : 'mdi-bell-outline'"
                    size="x-small"
                    variant="text"
                    :color="cat.isFollowing ? 'primary' : 'grey'"
                    @click.stop="toggleFollow(cat.slug)"
                  />
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Posts Feed -->
      <v-col cols="12" md="9">
        <!-- Top bar: search + sort + new post -->
        <div class="d-flex align-center flex-wrap ga-3 mb-4">
          <h1 class="text-h4 flex-grow-1">
            {{ activeCategoryName || $t('blog.title') }}
          </h1>

          <v-text-field
            v-model="searchQuery"
            :placeholder="$t('blog.search')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="max-width: 260px"
            @update:model-value="debouncedSearch"
            @click:clear="clearSearch"
          />

          <v-btn-toggle v-model="sortMode" mandatory density="compact" variant="outlined">
            <v-btn value="newest" size="small">
              <v-icon start size="16">mdi-clock-outline</v-icon>
              {{ $t('blog.newest') }}
            </v-btn>
            <v-btn value="popular" size="small">
              <v-icon start size="16">mdi-fire</v-icon>
              {{ $t('blog.popular') }}
            </v-btn>
          </v-btn-toggle>

          <v-btn color="primary" @click="showCreateDialog = true">
            <v-icon start>mdi-pencil-plus</v-icon>
            {{ $t('blog.writePost') }}
          </v-btn>
        </div>

        <!-- Loading -->
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          class="d-block mx-auto my-8"
        />

        <!-- Posts list -->
        <div v-else>
          <blog-post-card
            v-for="post in posts"
            :key="post.postId"
            :post="post"
            class="mb-4"
            @click="openPost(post)"
          />

          <v-alert v-if="posts.length === 0 && !loading" type="info" variant="tonal" class="mt-4">
            {{ $t('blog.noPosts') }}
          </v-alert>

          <!-- Load more -->
          <div v-if="pagination.page < pagination.totalPages" class="text-center mt-4">
            <v-btn variant="outlined" @click="loadMore" :loading="loading">
              {{ $t('blog.loadMore') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Create Post Dialog -->
    <blog-post-editor
      v-model="showCreateDialog"
      :categories="categories"
      @saved="onPostCreated"
    />
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import BlogPostCard from '../components/blog/BlogPostCard.vue'
import BlogPostEditor from '../components/blog/BlogPostEditor.vue'

const store = useStore()
const router = useRouter()

const categories = computed(() => store.getters['blog/categories'])
const posts = computed(() => store.getters['blog/posts'])
const pagination = computed(() => store.getters['blog/pagination'])
const loading = computed(() => store.getters['blog/loading'])
const filters = computed(() => store.getters['blog/filters'])

const showCreateDialog = ref(false)
const searchQuery = ref('')
const sortMode = ref('newest')

let searchTimeout = null

const { t } = useI18n()

const activeCategoryName = computed(() => {
  if (!filters.value.category) return null
  return t(`blog.categories.${filters.value.category}`)
})

const selectCategory = (slug) => {
  store.dispatch('blog/setFilters', { category: slug })
  // Mark as seen when user visits
  if (slug) store.dispatch('blog/markCategorySeen', slug)
}

const debouncedSearch = (val) => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    store.dispatch('blog/setFilters', { search: val || '' })
  }, 400)
}

const clearSearch = () => {
  store.dispatch('blog/setFilters', { search: '' })
}

const loadMore = () => {
  store.dispatch('blog/fetchPosts', { page: pagination.value.page + 1, append: true })
}

const toggleFollow = (slug) => {
  store.dispatch('blog/toggleFollow', slug)
}

const openPost = (post) => {
  router.push({ name: 'blogPost', params: { slug: post.slug } })
}

const onPostCreated = (result) => {
  showCreateDialog.value = false
  router.push({ name: 'blogPost', params: { slug: result.slug } })
}

watch(sortMode, (val) => {
  store.dispatch('blog/setFilters', { sort: val })
})

onMounted(() => {
  store.dispatch('blog/fetchCategories')
  store.dispatch('blog/fetchPosts')
})
</script>

<style scoped>
.blog-container {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 1.5rem;
}
.categories-card {
  position: sticky;
  top: 80px;
}
</style>